import { Dispatch } from 'redux';
import api from '../services/api';
import {
  FlagItem,
  LeasePriceByUnitFlagItem,
  LeasePriceFlagItem,
  TenantFlagItem,
} from '../types/flag.model';
import { handleError } from './dashboard';

export const FlagOfferActions = {
  SET_CURRENT_ITEM: 'SET_CURRENT_ITEM',
  IS_LOADING: 'FLAG_IS_LOADING',
  ADD_COMMENT: 'ADD_COMMENT',
  FLAG_ITEM: 'FLAG_ITEM',
  RESOLVE_FLAG: 'RESOLVE_FLAG',
  FETCH_COMMENTS: 'FETCH_COMMENTS',
};

const showNotification =
  (message: string, type: string) => (dispatch: Dispatch) =>
    dispatch({
      type: 'SHOW_NOTIFICATIONS',
      payload: {
        message: message,
        type: type,
      },
    });

const setLoading = (dispatch: Dispatch) => (isLoading: boolean) =>
  dispatch({
    type: FlagOfferActions.IS_LOADING,
    payload: isLoading,
  });

const getNotificationMessage = (flagItem: FlagItem) => {
  if (flagItem.type === 'Renewal') {
    return `Renewal Offers: ${
      flagItem.resolved ? 'flagged item is resolved' : 'flagged'
    } for Tenant_Code: ${(flagItem as TenantFlagItem).tenant.Tenant_Code}`;
  } else if (flagItem.type === 'Lease Price') {
    let leaseFlagItem = flagItem as LeasePriceFlagItem;
    return `Lease price ${
      flagItem.resolved ? 'flagged item is resolved' : 'flagged'
    } for ${leaseFlagItem.leasePrice.Property_Code}/${
      leaseFlagItem.leasePrice.UnitType
    }/${leaseFlagItem.leasePrice.Beds}`;
  } else if (flagItem.type === 'Lease Price Unit') {
    let leaseFlagItem = flagItem as LeasePriceByUnitFlagItem;
    return `Lease price ${
      flagItem.resolved ? 'flagged item is resolved' : 'flagged'
    } for ${leaseFlagItem.unitItem.Property_Code}/${
      leaseFlagItem.unitItem.UnitType
    }/${leaseFlagItem.unitItem.UnitId}`;
  }
};

export const setCurrentItem =
  (item: FlagItem | null = null) =>
  (dispatch: Dispatch) => {
    dispatch({
      type: FlagOfferActions.SET_CURRENT_ITEM,
      payload: item,
      meta: {
        track: true,
      },
    });
  };

export const flagTenant =
  (flagItem: FlagItem, comment?: string) => (dispatch: Dispatch) => {
    const loading = setLoading(dispatch);
    loading(true);
    api
      .flagItem({ info: flagItem.info, type: flagItem.type, comment: comment })
      .then((res) => {
        const updatedFlagItem = { ...flagItem };
        updatedFlagItem.id = res.data.flag_details.id;
        updatedFlagItem.userName = res.data.flag_details.UserName;
        updatedFlagItem.resolved = false;

        if (updatedFlagItem.type === 'Renewal') {
          const tenantFlagItem = updatedFlagItem as TenantFlagItem;
          dispatch({
            type: 'RENEWAL_UPDATE_FLAG',
            payload: tenantFlagItem,
            meta: {
              track: true,
            },
          });
        } else if (updatedFlagItem.type === 'Lease Price') {
          const leasePriceFlagItem = updatedFlagItem as LeasePriceFlagItem;
          dispatch({
            type: 'NEW_LEASE_UPDATE_FLAG',
            payload: leasePriceFlagItem,
            meta: {
              track: true,
            },
          });
        } else if (updatedFlagItem.type === 'Lease Price Unit') {
          const leasePriceFlagItem =
            updatedFlagItem as LeasePriceByUnitFlagItem;
          dispatch({
            type: 'NEW_LEASE_BY_UNIT_UPDATE_FLAG',
            payload: leasePriceFlagItem,
            meta: {
              track: true,
            },
          });
        }

        dispatch({
          type: FlagOfferActions.FLAG_ITEM,
          payload: { flagItem: updatedFlagItem, comment },
          meta: {
            track: true,
          },
        });
      })
      .catch((err) => {
        console.error(err);
        handleError(err, dispatch);
      })
      .finally(() => {
        loading(false);
      });
  };

export const fetchComments = (flagItem: FlagItem) => (dispatch: Dispatch) => {
  const loading = setLoading(dispatch);

  if (flagItem.id) {
    loading(true);

    api
      .fetchComments({ flagId: flagItem.id })
      .then((res) => {
        dispatch({
          type: FlagOfferActions.FETCH_COMMENTS,
          payload: {
            flagId: flagItem.id,
            comments: res.data.comments,
            isResolved: res.data.Resolved,
          },
          meta: {
            track: true,
          },
        });
      })
      .catch((err) => {
        console.error(err);
        handleError(err, dispatch);
      })
      .finally(() => {
        loading(false);
      });
  }
};

export const resolveFlag = (flagItem: FlagItem) => (dispatch: Dispatch) => {
  const loading = setLoading(dispatch);
  if (flagItem.id) {
    loading(true);
    api
      .resolveFlag({ flagId: flagItem.id })
      .then((res) => {
        const updateFlagItem = { ...flagItem };
        updateFlagItem.resolved = true;
        if (updateFlagItem.type === 'Renewal') {
          const tenantFlagItem = updateFlagItem as TenantFlagItem;
          dispatch({
            type: 'RENEWAL_UPDATE_FLAG',
            payload: tenantFlagItem,
            meta: {
              track: true,
            },
          });
        } else if (updateFlagItem.type === 'Lease Price') {
          const leasePriceFlagItem = updateFlagItem as LeasePriceFlagItem;
          dispatch({
            type: 'NEW_LEASE_UPDATE_FLAG',
            payload: leasePriceFlagItem,
            meta: {
              track: true,
            },
          });
        } else if (updateFlagItem.type === 'Lease Price Unit') {
          const leasePriceFlagItem = updateFlagItem as LeasePriceByUnitFlagItem;
          dispatch({
            type: 'NEW_LEASE_BY_UNIT_UPDATE_FLAG',
            payload: leasePriceFlagItem,
            meta: {
              track: true,
            },
          });
        }
        showNotification(
          getNotificationMessage(updateFlagItem),
          'success'
        )(dispatch);
        dispatch({
          type: FlagOfferActions.RESOLVE_FLAG,
          payload: updateFlagItem,
          meta: {
            track: true,
          },
        });
      })
      .catch((err) => {
        console.error(err);
        handleError(err, dispatch);
      })
      .finally(() => {
        loading(false);
      });
  }
};

export const addComment =
  (flagItem: FlagItem, comment: string) => (dispatch: Dispatch) => {
    const loading = setLoading(dispatch);
    if (flagItem.id) {
      loading(true);
      api
        .addComment({ flagId: flagItem.id, comment })
        .then((res) => {
          dispatch({
            type: FlagOfferActions.ADD_COMMENT,
            payload: { comment, UserName: res.data.comment.UserName },
            meta: {
              track: true,
            },
          });
        })
        .catch((err) => {
          console.error(err);
          handleError(err, dispatch);
        })
        .finally(() => {
          loading(false);
        });
    }
  };
